/**
 * Map API / network failures to merchant-facing copy.
 * Never surface SQL, stack traces, NATS, or raw HTTP bodies.
 */

import i18n from '../i18n';

const STATUS_KEYS = {
  400: 'errors.status.400',
  401: 'errors.status.401',
  403: 'errors.status.403',
  404: 'errors.status.404',
  409: 'errors.status.409',
  422: 'errors.status.422',
  429: 'errors.status.429',
  502: 'errors.status.502',
  503: 'errors.status.503',
  504: 'errors.status.504',
};

const KNOWN_PHRASES = [
  {
    test: /pin.?limit|restore point limit|max 7|max 1|named restore point/i,
    key: 'errors.known.pinLimit',
  },
  {
    test: /backup already in progress|already running/i,
    key: 'errors.known.alreadyRunning',
  },
  {
    test: /payment.?source|payment method|CHARGE_PAYMENT|402/i,
    key: 'errors.known.paymentMethod',
  },
  {
    test: /api key not configured|register/i,
    key: 'errors.known.connecting',
  },
  {
    test: /store not found|not connected/i,
    key: 'errors.known.notConnected',
  },
  {
    test: /restore point is not complete/i,
    key: 'errors.known.pointNotReady',
  },
  {
    test: /review.*restor|ORDERS_BACKUP_ONLY/i,
    key: 'errors.known.reviewsOnly',
  },
  {
    test: /failed to enqueue|nats/i,
    key: 'errors.known.enqueueFailed',
  },
];

const LEAKY = [
  /postgres/i,
  /sql/i,
  /ECONNREFUSED/i,
  /ENOTFOUND/i,
  /stack/i,
  /at Object\./i,
  /relation ["'].+["'] does not exist/i,
  /syntax error/i,
  /jetstream/i,
  /nats/i,
  /internal server/i,
  /ECONNRESET/i,
  /axios/i,
  /fetch failed/i,
  /networkerror/i,
  /bearer token/i,
  /cannot read prop/i,
  /undefined is not/i,
];

function looksLeaky(message) {
  if (!message || typeof message !== 'string') return true;
  if (message.length > 220) return true;
  return LEAKY.some((re) => re.test(message));
}

/**
 * @param {unknown} err
 * @param {string} [fallback]
 * @returns {string}
 */
export function userFacingError(err, fallback) {
  const resolvedFallback = fallback || i18n.t('errors.fallback');
  if (err == null) return resolvedFallback;

  const status = Number(err.status || err.statusCode || err.data?.statusCode || 0);
  const raw =
    (typeof err === 'string' && err) ||
    err.data?.error ||
    err.data?.message ||
    err.message ||
    '';

  const rawStr = String(raw || '').trim();

  for (const rule of KNOWN_PHRASES) {
    if (rule.test.test(rawStr) || (err.code && rule.test.test(String(err.code)))) {
      return i18n.t(rule.key);
    }
  }

  if (status === 409) {
    return i18n.t(STATUS_KEYS[409]);
  }

  if (status && STATUS_KEYS[status] && (looksLeaky(rawStr) || !rawStr)) {
    return i18n.t(STATUS_KEYS[status]);
  }

  if (rawStr && !looksLeaky(rawStr)) {
    return rawStr.replace(/^Error:\s*/i, '');
  }

  if (status && STATUS_KEYS[status]) {
    return i18n.t(STATUS_KEYS[status]);
  }

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return i18n.t('errors.offline');
  }

  return resolvedFallback;
}

export function logQuiet(context, err) {
  // Keep diagnostics in console for support; never show to merchant.
  // eslint-disable-next-line no-console
  console.warn(`[Backuply] ${context}`, err);
}
